import React from 'react'

import type { SignatureContent } from '../../../common/models/signature-tool/signature-tool.js'
import type { CustomLineType } from './signature.js'

export interface SignatureNopeProps extends SignatureContent {
    signatureRef?: React.Ref<HTMLTableElement>
}

// The 2026 NOPE brand purple for email/web links.
const LINK_COLOR = '#8B5CF6'

// Nele's requested stack: San Francisco → Segoe UI (Century Gothic deliberately
// skipped — a custom font can't be relied on in mail clients). The system-font
// keywords resolve to San Francisco on Apple platforms. Gmail's sanitizer drops
// the non-standard -apple-system/BlinkMacSystemFont keywords but keeps the
// standard system-ui, and 'Helvetica Neue' backstops macOS viewers — both have
// a real Light face, which font-weight 300 needs to render light (browsers
// synthesize bold, never light; Arial fallback would show 300 as regular).
const FONT_FAMILY =
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif"

// Gmail's signature editor deletes the `font-weight` declaration when pasting
// (its editor model only knows bold/not-bold), so the light name can't rely on
// `font-weight: 300` alone. The light cut is instead selected by family NAME —
// 'Segoe UI Light' (Windows) and 'Helvetica Neue Light' (macOS; San Francisco's
// light face has no CSS-reachable name) are registered as their own families,
// and Gmail preserves font-family lists. The weight rule stays as well for the
// clients that do honour it; unmatched platforms (e.g. Android) degrade to the
// regular stack.
const NAME_FONT_FAMILY = `'Segoe UI Light', 'HelveticaNeue-Light', 'Helvetica Neue Light', ${FONT_FAMILY}`

// Logo images ship with the Hub server (src/server/assets/signature/, served at
// /cvi/signature/*) so the copied signature points at a stable public URL. Both
// PNGs are transparent with a white sticker shape baked in behind the lettering:
// near-invisible on a light email, a sticker on a dark one — one asset works in
// both modes. Dimensions are the CSS px of the 2× retina files.
const LOGOS = {
    nope: {
        url: 'https://nope.ee',
        file: 'nope-logo-2x.png',
        width: '57',
        height: '18',
        alt: 'Nope OÜ',
    },
    alliance: {
        url: 'https://playandnope.com',
        file: 'alliance-2x.png',
        width: '74',
        height: '46',
        alt: 'part of play & nope alliance',
    },
} as const

const renderContactLine = (
    label: string,
    value: React.ReactNode,
    key?: React.Key,
): React.ReactNode => (
    <p key={key} style={styles.line}>
        {label}: {value}
    </p>
)

const renderCustomLine = (
    line: CustomLineType,
    index: number,
    textColor: string,
): React.ReactNode => {
    if (!line.key || line.key.trim() === '') {
        return null
    }

    // tel: links stay in the text colour — the design shows the phone number
    // unhighlighted, but it should still be tappable. The colour is an explicit
    // hex (not `inherit`) because mail clients are unreliable with inheritance.
    const value = line.link ? (
        <a
            href={line.link}
            style={
                line.link.startsWith('tel:')
                    ? { textDecoration: 'none', color: textColor }
                    : styles.link
            }
        >
            {line.value || line.key}
        </a>
    ) : (
        line.value
    )

    return renderContactLine(line.key, value, index)
}

const renderLogo = (config: (typeof LOGOS)[keyof typeof LOGOS], margin: string) => (
    <p style={{ margin, padding: 0, lineHeight: '0' }}>
        <a href={config.url} style={styles.noUnderline}>
            <img
                src={`${window.location.origin}/cvi/signature/${config.file}`}
                width={config.width}
                height={config.height}
                alt={config.alt}
                style={styles.blockImage}
            />
        </a>
    </p>
)

/**
 * The 2026 NOPE email signature (TASK-344). Differs from the shared `Signature`
 * renderer enough to be its own layout: the photo is a pre-composited circle +
 * doodle PNG top-aligned beside all the text, the name is large and regular
 * weight, contact rows are 10px with custom lines (Phone) before Email/Web, and
 * the sticker-style NOPE + alliance logos are Hub-hosted.
 */
export const SignatureNope: React.FC<SignatureNopeProps> = ({
    name,
    title,
    email,
    website,
    imageUrl,
    customLines = [],
    nopeLogo,
    allianceLogo,
    signatureRef,
    isDark,
}) => {
    const textColor = isDark ? '#ffffff' : '#2c2c2c'

    return (
        <table
            ref={signatureRef}
            style={{
                fontFamily: FONT_FAMILY,
                fontSize: '10px',
                borderCollapse: 'collapse',
                margin: '0 0 0 24px',
                color: textColor,
            }}
        >
            <tbody>
                <tr>
                    {imageUrl && (
                        <td style={{ width: '68px', verticalAlign: 'top', padding: '0 13px 0 0' }}>
                            <img
                                src={imageUrl}
                                alt={name}
                                width="68"
                                height="74"
                                style={styles.blockImage}
                            />
                        </td>
                    )}

                    <td style={{ verticalAlign: 'top', padding: 0 }}>
                        <p style={styles.name}>{name}</p>
                        <p style={styles.title}>{title}</p>

                        {customLines.map((line, i) => renderCustomLine(line, i, textColor))}
                        {email &&
                            renderContactLine(
                                'Email',
                                <a href={`mailto:${email}`} style={styles.link}>
                                    {email}
                                </a>,
                            )}
                        {website &&
                            renderContactLine(
                                'Web',
                                <a href={`https://${website}`} style={styles.link}>
                                    {website}
                                </a>,
                            )}

                        {nopeLogo && renderLogo(LOGOS.nope, '23px 0 0 0')}
                        {allianceLogo && renderLogo(LOGOS.alliance, '12px 0 0 0')}
                    </td>
                </tr>
            </tbody>
        </table>
    )
}

// Vertical rhythm is tuned to the measured ink positions of the Figma design
// (avatar top = 0): name ink at +5, 12px ink gap to the title, contact rows
// from +64 with a 15px pitch, logos at +128/+158.
const styles = {
    name: {
        fontFamily: NAME_FONT_FAMILY,
        fontSize: '22px',
        lineHeight: '27px',
        fontWeight: 300,
        margin: '0',
        padding: 0,
    },
    title: {
        fontFamily: FONT_FAMILY,
        fontSize: '12px',
        lineHeight: '20px',
        margin: '0 0 12px 0',
        padding: 0,
    },
    line: {
        fontFamily: FONT_FAMILY,
        fontSize: '10px',
        lineHeight: '15px',
        margin: 0,
        padding: 0,
    },
    link: {
        textDecoration: 'none',
        color: LINK_COLOR,
    },
    noUnderline: {
        textDecoration: 'none',
    },
    blockImage: {
        display: 'block',
        border: 0,
    },
} as const
